import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Calendar, Trophy, Star, ChevronRight, Settings2, Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useOlympicsSchedule } from "@/hooks/useOlympicsSchedule";
import { useFavoriteNationAlerts } from "@/hooks/useFavoriteNationAlerts";
import { format, differenceInCalendarDays } from "date-fns";
import type { HockeyNation } from "@/data/hockeyNations";
import type { OlympicGame } from "@/data/olympics2026Schedule";

interface NationHeroSectionProps {
  nation: HockeyNation;
  nationName: string;
  onEditNation: () => void;
  canEdit: boolean;
}

// Map ISO codes to country names used in Olympics schedule
const getNationName = (isoCode: string): string | null => {
  const mapping: Record<string, string> = {
    CAN: "Canada",
    USA: "United States",
    SWE: "Sweden",
    FIN: "Finland",
    RUS: "Russia",
    CZE: "Czechia",
    CHE: "Switzerland",
    DEU: "Germany",
    SVK: "Slovakia",
    LVA: "Latvia",
    DNK: "Denmark",
    NOR: "Norway",
    FRA: "France",
    AUT: "Austria",
    KAZ: "Kazakhstan",
    JPN: "Japan",
    ITA: "Italy",
  };
  return mapping[isoCode] || null;
};

const NationGameCard = ({ game }: { game: OlympicGame & { startsAt: Date } }) => {
  // Compare calendar dates without timezone issues by parsing date string to local midnight
  const [y, m, d] = game.date.split('-').map(Number);
  const gameDate = new Date(y, m - 1, d);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const daysUntil = differenceInCalendarDays(gameDate, today);
  
  return (
    <div className="bg-secondary/50 rounded-xl p-4 border border-border hover:border-primary/30 transition-all">
      <div className="flex items-center justify-between mb-3">
        <Badge variant="outline" className="text-xs">
          {game.tournament === "women" ? "👩‍🦰 Women" : "🏒 Men"} • {game.stage}
        </Badge>
        <span className="text-xs text-primary font-medium">
          {daysUntil === 0 ? "Today" : daysUntil === 1 ? "Tomorrow" : `In ${daysUntil} days`}
        </span>
      </div>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-2xl">{game.homeFlag}</span>
          <span className="font-medium text-foreground">{game.homeTeam}</span>
        </div>
        <span className="text-muted-foreground text-sm">vs</span>
        <div className="flex items-center gap-2">
          <span className="font-medium text-foreground">{game.awayTeam}</span>
          <span className="text-2xl">{game.awayFlag}</span>
        </div>
      </div>
      <div className="flex items-center justify-between text-xs text-muted-foreground mt-3 pt-3 border-t border-border/50">
        <div className="flex items-center gap-1">
          <Calendar className="w-3 h-3" />
          <span>{format(game.startsAt, "MMM d, yyyy")} at {game.time} Europe/Rome</span>
        </div>
        <span>{game.venue}</span>
      </div>
    </div>
  );
};

export default function NationHeroSection({ nation, nationName, onEditNation, canEdit }: NationHeroSectionProps) {
  const { data: olympicsData } = useOlympicsSchedule();
  const { alertsEnabled, toggleAlerts } = useFavoriteNationAlerts();

  // Get upcoming games for the nation
  const upcomingGames = useMemo(() => {
    if (!olympicsData) return [];
    
    const allGames = [...olympicsData.womensSchedule, ...olympicsData.mensSchedule];
    const scheduleNationName = getNationName(nation.code);
    const now = new Date();

    return allGames
      .filter((game) => {
        const isPlaying = game.homeTeam === scheduleNationName || game.awayTeam === scheduleNationName;
        if (!isPlaying) return false;
        const gameDateTime = new Date(`${game.date}T${game.time}:00+01:00`);
        return gameDateTime > now;
      })
      .map((game) => ({
        ...game,
        startsAt: new Date(`${game.date}T${game.time}:00+01:00`),
      }))
      .sort((a, b) => a.startsAt.getTime() - b.startsAt.getTime());
  }, [olympicsData, nation.code]);

  const nextGame = upcomingGames[0];
  const totalGames = upcomingGames.length;

  return (
    <section className="mb-10">
      {/* Hero Card */}
      <Card className="relative overflow-hidden border-primary/30 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent">
        {/* Background decoration */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        
        <CardContent className="relative p-6 md:p-8">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
            {/* Nation Info */}
            <div className="flex-1">
              <div className="flex items-center gap-4 mb-4">
                <div className="text-6xl md:text-7xl">{nation.flag}</div>
                <div>
                  <p className="text-sm text-primary font-medium uppercase tracking-wider mb-1">Your Nation</p>
                  <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground">
                    {nation.name}
                  </h2>
                </div>
              </div>
              
              <p className="text-muted-foreground mb-4 max-w-lg">
                Track {nation.name}'s journey in the 2026 Milano Cortina Olympics. 
                {totalGames > 0 
                  ? ` ${totalGames} upcoming game${totalGames !== 1 ? 's' : ''} scheduled.`
                  : " No scheduled games yet."
                }
              </p>

              {/* Action Buttons */}
              <div className="flex flex-wrap items-center gap-3">
                <Button 
                  variant={alertsEnabled ? "default" : "outline"} 
                  size="sm" 
                  onClick={toggleAlerts}
                  className="gap-2"
                >
                  <Bell className={`w-4 h-4 ${alertsEnabled ? "fill-current" : ""}`} />
                  {alertsEnabled ? "Alerts On" : "Enable Alerts"}
                </Button>
                
                <Link to="/#international-tournaments">
                  <Button variant="outline" size="sm" className="gap-2">
                    <Trophy className="w-4 h-4" />
                    Tournament Results
                  </Button>
                </Link>
                
                {canEdit && (
                  <Button variant="ghost" size="sm" onClick={onEditNation} className="gap-2 text-muted-foreground hover:text-foreground">
                    <Settings2 className="w-4 h-4" />
                    Change Nation
                  </Button>
                )}
              </div>
            </div>

            {/* Next Game Preview */}
            {nextGame && (
              <div className="lg:w-80">
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-2">
                  <Star className="w-3 h-3 text-primary fill-primary" />
                  Next Match
                </p>
                <NationGameCard game={nextGame} />
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* More Games Preview */}
      {upcomingGames.length > 1 && (
        <div className="mt-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-muted-foreground">
              Upcoming {nation.name} Games
            </h3>
            <Link 
              to="/#international-tournaments" 
              className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors"
            >
              View all <ChevronRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {upcomingGames.slice(1, 4).map((game) => (
              <NationGameCard key={game.id} game={game} />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
